const get = require('lodash/get')
const keyBy = require('lodash/keyBy')
const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')

// Flattens an alias map (e.g. {one: "GHSC-MAL0084A"} or {one: [...], epn: [...]})
// into a plain, upper-cased list of alias code strings.
const aliasValuesOf = (aliasMap) => {
  return Object.values(aliasMap || {})
    .flatMap(entry => (Array.isArray(entry) ? entry : [entry]))
    .filter(value => typeof value === 'string')
    .map(value => value.toUpperCase())
}

// A batch's productId can be an alias code rather than a real product _id
// (e.g. set from an SFTP/alias import). Aliases aren't just a case variant of
// the real _id - a generic parent product's `alias.<name>` array can list
// several manufacturer-specific codes (e.g. GHSC-MAL0084, GHSC-MAL0084A,
// GHSC-MAL0084B, ...) that all resolve to that one product doc, whose real
// _id may not resemble the alias string at all. Searches against already-loaded
// products (productsById) rather than issuing a new fetch.
//
// When an alias matches more than one product, prefer the manufacturer-specific
// one - mirrors pickAliasedProduct in api/lib/product/api/read/get.js, which
// resolves this same ambiguity for product.getByAlias.
const findProductByAlias = (productsById, aliasValues) => {
  if (!aliasValues.length) {
    return undefined
  }

  const matches = Object.values(productsById).filter(product =>
    aliasValuesOf(product.alias).some(alias => aliasValues.includes(alias))
  )
  if (!matches.length) {
    return undefined
  }
  if (matches.length === 1) {
    return matches[0]
  }

  const isManufacturerSpecific = doc =>
    doc.genericParent && doc.code && doc.genericParent.toUpperCase() !== doc.code.toUpperCase()
  const specificMatches = matches.filter(isManufacturerSpecific)
  const candidates = specificMatches.length ? specificMatches : matches
  const exactCodeMatch = candidates.find(doc => doc.code && aliasValues.includes(doc.code.toUpperCase()))
  return exactCodeMatch || candidates[0]
}

function isOrphanShipment (shipment) {
  if (!shipment || (shipment.status !== 'arrived' && shipment.status !== 'received')) {
    return false
  } else {
    // If we have a new, sent, or pre-advice in our history, we're not an orphan.
    return !Object.keys(shipment.history).find(snapshotId => {
      const status = snapshotId.split('status:')[1].split(':')[0]
      return (status === 'new' || status === 'sent' || status === 'pre-advice')
    })
  }
}

function canShipmentBeMarkedVoid (shipment) {
  return (shipment.status === 'new' || shipment.status === 'pre-advice' || isOrphanShipment(shipment))
}

/**
 * HEADS UP: this does not return batch expiries as we're not starting with
 * them in shelf life. But if needed, just add master data batch info.
 * @param shipment a store api shipment entity
 * @param productsById master data object of products
 * @return a list of batches with shipment counts and product info
 */
function getShipmentBatches (shipment, productsById) {
  // Get array of batches on shipment with master data product prop
  return Object.keys(shipment.counts)
    .map(batchId => {
      const [productId] = batchId.split(':manufacturer')
      const product = productsById[productId] ||
        productsById[productId.toLowerCase()] ||
        findProductByAlias(productsById, [productId.replace(/^product:/i, '').toUpperCase()])
      const batch = {batchId, ...shipment.counts[batchId], ...product}
      const total = getOriginalQuantity(shipment, batchId)
      return {...batch, id: batchId, total, picked: batch.quantity}
    })
    // sort by product name
    .sort((a, b) => a.name - b.name)
}

// This expects to be working on only a status new snapshot
// see picklist reducer logic if this ever changes for SL
function getOriginalQuantity (shipment, batchId) {
  const prevSnapshot = shipment.history[shipment.snapshotId]
  // Use array-notation get so dots in batchId (e.g. "BAT.ADV8") are treated as
  // literal key characters, not nested-path separators.
  return get(prevSnapshot, ['counts', batchId, 'quantity'], 0)
}

function transformToReturnBatch (returnShipment, batches = [], postDelivery = false) {
  return batches.reduce((acc, val) => {
    const item = returnShipment.counts[val.id]
    const quantity = val.total - val.picked
    if ((item && quantity > 0) || postDelivery) {
      acc.push({
        paymentType: item.paymentType,
        hideManifest: false,
        name: val.name,
        productId: val._id,
        id: val._id,
        total: val.total,
        checked: item.checked,
        quantity: postDelivery ? 0 : quantity,
        picked: item.quantity,
        batchId: val.batchId,
        unitSellPrice: val.unitSellPrice,
        unitSellPriceReferenceSku: val.unitSellPriceReferenceSku,
        vat: val.vat,
        unitSellPriceReferenceId: val.unitSellPriceReferenceId,
        priceSource: val.priceSource
      })
    }
    return acc
  }, [])
}

const transformReceivedCount = (receivedCount, batches = []) => {
  const batchesByProduct = keyBy(batches, 'batchId')
  return Object.keys(receivedCount).reduce((acc, batch) => {
    const productBatch = batchesByProduct[batch]
    acc[batch] = receivedCount[batch]
    // Quantity that was sent from warehouse
    // We perform an automatic return with the difference
    acc[batch].quantity = productBatch.total
    return acc
  }, {})
}

function buildOtp (otpRow) {
  const key = otpRow.otp_key || undefined
  const hasKeyAndPassword = Boolean(key && otpRow.password)
  return omitBy({
    key,
    userFailReason: otpRow.user_fail_reason,
    hasKeyAndPassword
  }, isNil)
}

module.exports = {
  isOrphanShipment,
  canShipmentBeMarkedVoid,
  getShipmentBatches,
  getOriginalQuantity,
  transformToReturnBatch,
  transformReceivedCount,
  buildOtp,
  findProductByAlias
}
